import axios from 'axios';
import { FETCH_AWARD, PARTICIPANT_COMPANIES } from './types';
import api from '../../../utils/api';

// In-flight deduplication: concurrent callers share one Promise.
// Completed cache: sequential callers skip re-fetching already-loaded data.
// Keys in use:
//   'award:{awardId}'              — fetchAward
//   'registrationList:{awardId}'  — fetchRegistrationList
//   'categories:{awardId}'        — fetchCategories
//   'agenda:{awardId}'            — fetchAgenda
//   'agendaMaps:{awardId}'        — fetchAgendaMaps
//   'juries:{awardId}'            — fetchJuries
const _pendingActions = {};
const _completedActions = {};

export default {
  async fetchAwards(_, page) {
    const { data } = await axios(api.awards.fetchAwards(page));
    return data;
  },
  async fetchAward({ commit, state }, { awardId, router }) {
    if (!awardId) return;
    if (state.award && String(state.award.id) === String(awardId)) return state.award;
    const key = `award:${awardId}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.awards.fetchAward(awardId));
        commit(FETCH_AWARD, { award: data });
        return data;
      } catch (error) {
        if (error.response && error.response.status === 404 && router) {
          router.push({ name: 'ccAllEvents', query: { 'inactive-event-redirect': 'true' } });
        }
        throw error;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchJuries(_, awardId) {
    const key = `juries:${awardId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.awards.fetchJuries(awardId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchCategories(_, awardId) {
    const key = `categories:${awardId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.awards.fetchCategories(awardId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchAgenda(_, awardId) {
    const key = `agenda:${awardId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.awards.fetchAgenda(awardId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchAgendaMaps(_, awardId) {
    const key = `agendaMaps:${awardId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.awards.fetchAgendaMaps(awardId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchGallery(_, awardId) {
    const { data } = await axios(api.awards.fetchGallery(awardId));
    return data.results;
  },
  async fetchAwardCategories(_, awardId) {
    const { data } = await axios(api.awards.fetchAwardCategories(awardId));
    return data.results;
  },
  async fetchAwardSponsors(_, categoryId) {
    const { data } = await axios(api.awards.fetchAwardSponsors(categoryId));
    return data.results;
  },
  async fetchShortlisted(_, categoryId) {
    const { data } = await axios(api.awards.fetchShortlisted(categoryId));
    return data.results;
  },
  async fetchRegistrationList(_, awardId) {
    const key = `registrationList:${awardId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.awards.fetchRegistrationList(awardId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchParticipantCompanies({ commit }, fetchData) {
    const { data } = await axios(api.awards.participantCompanies(fetchData));
    commit(PARTICIPANT_COMPANIES, { data });
    return data;
  },
  async awardBooking({}, bookingDetails) {
    const { data } = await axios(api.awards.awardBooking(bookingDetails));
    return data;
  },
  async fetchBookedAwards({}, customerId) {
    const { data } = await axios(api.awards.fetchBookedAwards(customerId));
    return data.results;
  },

  async applyForCategory(_, application) {
    const { data } = await axios(api.awards.applyForCategory(application));
    return data;
  },
  async editApplication(_, application) {
    const { data } = await axios(api.awards.editApplication(application));
    return data;
  },
  async deleteApplication(_, id) {
    await axios(api.awards.deleteApplication(id));
  },
  async applyDocumentForCategory(_, data) {
    await axios(api.awards.applyDocumentForCategory(data));
  },
  async editApplicationDocument(_, application) {
    const { data } = await axios(api.awards.editApplicationDocument(application));
    return data;
  },
  async deleteApplicationDocument(_, id) {
    await axios(api.awards.deleteApplicationDocument(id));
  },
  async fetchMyCompanyApplications(_, awardId) {
    const { data } = await axios(api.awards.fetchMyCompanyApplications(awardId));
    return data;
  },
  async sendEmailToApplicant(_, emailData) {
    const { data } = await axios(api.awards.sendEmailToApplicant(emailData));
    return data;
  },
  async fetchAwardQuestions(_, { awardId, customerId }) {
    const { data } = await axios(api.awards.fetchAwardQuestions(awardId, customerId));
    return data;
  },
  async submitQuestionsAnswers(_, { awardId, customerId, answers }) {
    const { data } = await axios(api.awards.submitQuestionsAnswers(awardId, customerId, answers));
    return data;
  },
  async fetchSubmission(_, submissionId) {
    const { data } = await axios(api.awards.fetchSubmission(submissionId));
    return data;
  },
  async fetchGCCWinners(_, awardId) {
    const { data } = await axios(api.awards.fetchGCCWinners(awardId));
    return data.results;
  }
};
